//@flow
import * as Immutable from 'immutable';

import { LineItemPart } from 'common/models/flow/LineItemPart';
import type { LineItemPartModelType } from 'common/models/flow/LineItemPart';

import { Volume } from './Volume';

import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';
import I18nHelper from 'common/helpers/I18nHelper';

//Validation stuff
import {
    validate,
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import {
    Dimensions,
    sourceUnitForWeight,
    preferredUnitsForWeight
} from './Dimensions';
import LineItemHelper from 'common/entity/helpers/LineItemHelper';

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalLineItemPart extends LineItemPart {
    static fromJS(json: LineItemPartModelType): InternalLineItemPart {
        const state: Object = Object.assign({}, json);
        const { volume, dimensions } = LineItemHelper.updateDimensions(state);
        state.volume = Volume.fromJS(state.volume);
        state.dimensions = state.dimensions
            ? Dimensions.fromJS(state.dimensions)
            : state.dimensions;
        if (volume && dimensions) {
            state.volume = volume;
            state.dimensions = dimensions;
        }
        return new this(Immutable.Map(state));
    }
    @softValidateAll
    hasValidationErrors() {
        return (
            this.volume.hasValidationErrors() ||
            this.dimensions?.hasValidationErrors()
        );
    }

    @validateAll
    setAllValidationErrors(): this {
        var self = this;
        const { volume, dimensions } = this;
        if (volume) {
            self = self.setVolume(volume.setAllValidationErrors());
        }
        if (dimensions) {
            self = self.setDimensions(dimensions.setAllValidationErrors());
        }
        return self;
    }

    @validate
    setWeight(value: ?number): this {
        return super.setWeight(value);
    }

    validateWeight() {
        if (
            !DataUtils.isEmpty(this.weight) &&
            !NumberUtils.isPositiveNumber(this.weight)
        ) {
            return nsTranslate('skuValidationErrors.weightNotANumber');
        }
        return null;
    }

    getTotalVolume(unitConverter: UnitConversionHelper) {
        return this.volume.getTotalVolume(unitConverter);
    }

    getPreferredUnitForWeight(unitConverter: UnitConversionHelper) {
        return unitConverter.getPreferredUnit({
            preferredUnits: preferredUnitsForWeight,
            sourceUnit: sourceUnitForWeight
        });
    }

    getPreferredUnitTextForWeight(unitConverter: UnitConversionHelper) {
        return unitConverter.getConvertedUnitDisplayText({
            preferredUnits: preferredUnitsForWeight,
            sourceUnit: sourceUnitForWeight,
            pluralizeUnit: false
        });
    }

    convertToPreferredUnit(unitConverter: UnitConversionHelper): this {
        let updatedPart = this;
        const { weight, volume, dimensions } = this;
        if (NumberUtils.isNumeric(weight)) {
            const convertWeight = value =>
                unitConverter.convertValue({
                    value: NumberUtils.toNumber(value),
                    sourceUnit: sourceUnitForWeight,
                    preferredUnits: preferredUnitsForWeight
                }).value;
            updatedPart = updatedPart.setWeight(convertWeight(weight));
        }
        if (volume) {
            updatedPart = updatedPart.setVolume(
                volume.convertToPreferredUnit(unitConverter)
            );
        }
        if (dimensions) {
            updatedPart = updatedPart.setDimensions(
                dimensions.convertToPreferredUnit(unitConverter)
            );
        }

        return updatedPart;
    }

    convertToSourceUnit(unitConverter: UnitConversionHelper): this {
        let updatedPart = this;
        const { weight, volume, dimensions } = this;
        if (NumberUtils.isNumeric(weight)) {
            const preferredUnit = this.getPreferredUnitForWeight(unitConverter);
            const convertWeight = value =>
                unitConverter.basicConvert({
                    value: NumberUtils.toNumber(value),
                    sourceUnit: preferredUnit,
                    targetUnit: sourceUnitForWeight
                });
            updatedPart = updatedPart.setWeight(convertWeight(weight));
        }
        if (volume.value && !volume.hasValidationErrors()) {
            updatedPart = updatedPart.setVolume(
                volume.convertToSourceUnit(unitConverter)
            );
        }
        if (dimensions) {
            updatedPart = updatedPart.setDimensions(
                dimensions.convertToSourceUnit(unitConverter)
            );
        }
        return updatedPart;
    }

    sanitize(entity, unitConverter) {
        const { volume, dimensions, weight } = this.convertToSourceUnit(
            unitConverter
        );
        return entity
            .setVolume(volume)
            .setDimensions(dimensions)
            .setWeight(weight);
    }

    defaultSanitize(oldEntity, unitConverter): ?this {
        let sanitizedLineItem = this.sanitize(oldEntity, unitConverter);
        sanitizedLineItem = super.defaultSanitize(sanitizedLineItem);
        return sanitizedLineItem;
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    get dimensions(): ?Dimensions {
        return this._state.get('dimensions');
    }
}

export { InternalLineItemPart as LineItemPart };
